import React from 'react';
import { Target, TrendingUp, TrendingDown, Award, Zap, Activity, CheckCircle } from 'lucide-react';
import { BestSetup } from '../../types';

interface BestSetupsDashboardProps {
  data: BestSetup[];
}

export const BestSetupsDashboard: React.FC<BestSetupsDashboardProps> = ({ data }) => {
  const getIconForSetup = (title: string) => {
    switch (title) {
      case 'Long Bias': return TrendingUp;
      case 'Short Bias': return TrendingDown;
      case 'Più Frequente': return Award;
      case 'Hidden Gem': return Zap;
      case 'Volume Monster': return Activity;
      case 'Consistent': return CheckCircle;
      default: return Target;
    }
  };

  const getColorForSetup = (title: string) => {
    switch (title) {
      case 'Long Bias': return 'green';
      case 'Short Bias': return 'red';
      case 'Più Frequente': return 'blue';
      case 'Hidden Gem': return 'purple';
      case 'Volume Monster': return 'yellow';
      case 'Consistent': return 'teal';
      default: return 'gray';
    }
  };

  const getColorClasses = (color: string) => {
    const classes = {
      bg: '',
      text: '',
      border: '',
      iconBg: ''
    };

    switch (color) {
      case 'green':
        classes.bg = 'bg-green-500/10';
        classes.text = 'text-green-400';
        classes.border = 'border-green-500/30';
        classes.iconBg = 'bg-green-500/20';
        break;
      case 'red':
        classes.bg = 'bg-red-500/10';
        classes.text = 'text-red-400';
        classes.border = 'border-red-500/30';
        classes.iconBg = 'bg-red-500/20';
        break;
      case 'blue':
        classes.bg = 'bg-blue-500/10';
        classes.text = 'text-blue-400';
        classes.border = 'border-blue-500/30';
        classes.iconBg = 'bg-blue-500/20';
        break;
      case 'purple':
        classes.bg = 'bg-purple-500/10';
        classes.text = 'text-purple-400';
        classes.border = 'border-purple-500/30';
        classes.iconBg = 'bg-purple-500/20';
        break;
      case 'yellow':
        classes.bg = 'bg-yellow-500/10';
        classes.text = 'text-yellow-400';
        classes.border = 'border-yellow-500/30';
        classes.iconBg = 'bg-yellow-500/20';
        break;
      case 'teal':
        classes.bg = 'bg-teal-500/10';
        classes.text = 'text-teal-400';
        classes.border = 'border-teal-500/30';
        classes.iconBg = 'bg-teal-500/20';
        break;
      default:
        classes.bg = 'bg-gray-500/10';
        classes.text = 'text-gray-400';
        classes.border = 'border-gray-500/30';
        classes.iconBg = 'bg-gray-500/20';
    }

    return classes;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {data.map((setup, index) => {
        const Icon = getIconForSetup(setup.title);
        const color = getColorForSetup(setup.title);
        const colorClasses = getColorClasses(color);

        return (
          <div
            key={index}
            className={`premium-chart-card border-2 ${colorClasses.border} ${colorClasses.bg} hover:shadow-lg transition-all`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`p-3 rounded-lg ${colorClasses.iconBg}`}>
                  <Icon className={`h-6 w-6 ${colorClasses.text}`} />
                </div>
                <div>
                  <h4 className={`text-lg font-bold ${colorClasses.text}`}>{setup.title}</h4>
                  <p className="text-xs text-gray-400 mt-1">{setup.description}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-400 mb-1">Success Rate</p>
                <p className={`text-xl font-bold ${colorClasses.text}`}>
                  {setup.successRate.toFixed(1)}%
                </p>
              </div>

              <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-400 mb-1">Avg Spike</p>
                <p className={`text-xl font-bold ${colorClasses.text}`}>
                  {setup.avgSpike.toFixed(1)}%
                </p>
              </div>

              <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-400 mb-1">Count</p>
                <p className={`text-xl font-bold ${colorClasses.text}`}>
                  {setup.count}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <h5 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                Parametri Setup
              </h5>
              {Object.entries(setup.parameters).map(([key, value], pIndex) => {
                const isBias = key === 'Bias Trading';
                const biasColor = value === 'LONG' ? 'text-green-400' : value === 'SHORT' ? 'text-red-400' : 'text-gray-300';

                return (
                  <div
                    key={pIndex}
                    className="flex items-center justify-between py-2 px-3 bg-gray-800/30 rounded hover:bg-gray-800/50 transition-colors"
                  >
                    <span className="text-xs text-gray-400">{key}</span>
                    <span className={`text-sm font-semibold ${
                      isBias ? biasColor : 'text-white'
                    }`}>
                      {value}
                    </span>
                  </div>
                );
              })}
            </div>

            {setup.variance > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-700">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">Varianza</span>
                  <span className="text-sm font-semibold text-white">{setup.variance.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
